import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Dropdown, type DropdownChangeEvent, type DropdownProps } from "primereact/dropdown";
import { Button } from "primereact/button";
import { FilterService } from "primereact/api";
import type { SelectItem } from "primereact/selectitem";

export type CreatableSelectProps = DropdownProps;

// Single-select counterpart to CreatableMultiSelect: a PrimeReact Dropdown
// whose filter text can be added as a new option when it matches nothing, via
// the button in the empty-filter message or by pressing Enter in the filter
// input (Tab can't reach the panel button, so the Enter path is what keeps
// this keyboard-accessible).
export function CreatableSelect({
  options = [],
  value,
  onChange,
  pt,
  ...dropdownProps
}: CreatableSelectProps) {
  const dropdownRef = useRef<Dropdown>(null);
  const [customOptions, setCustomOptions] = useState<SelectItem[]>([]);
  const [filterValue, setFilterValue] = useState("");

  const allOptions = useMemo(() => {
    const known = [...options, ...customOptions];
    // a selected value saved outside the known lists still needs an option so
    // its label renders
    const unlisted =
      value && !known.some(option => option.value === value) ? [{ label: value, value }] : [];

    return [...known, ...unlisted];
  }, [options, customOptions, value]);

  const query = filterValue.trim();
  const hasMatches = FilterService.filter(allOptions, ["label"], query, "contains").length > 0;

  const addFilterValueAsOption = () => {
    if (!query) return;

    const newValue = query.toLowerCase().replace(/\s/g, "_");
    if (!allOptions.some(option => option.value === newValue)) {
      setCustomOptions(prev => [...prev, { label: query, value: newValue }]);
    }
    onChange?.({ value: newValue } as DropdownChangeEvent);
    dropdownRef.current?.hide();
  };

  const onFilterInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && query && !hasMatches) {
      addFilterValueAsOption();
    }
  };

  return (
    <Dropdown
      {...dropdownProps}
      ref={dropdownRef}
      value={value}
      options={allOptions}
      onChange={onChange}
      filter
      resetFilterOnHide
      onFilter={e => setFilterValue(e.filter)}
      onHide={() => setFilterValue("")}
      emptyFilterMessage={
        <Button
          type="button"
          link
          className="p-0"
          label={`Add "${query}"`}
          onClick={addFilterValueAsOption}
        />
      }
      pt={{ ...pt, filterInput: { onKeyDown: onFilterInputKeyDown } }}
    />
  );
}
